import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as BUTTON_CONFIG from '../../../../../constants/button/index';
import { ModalBaseClass } from '../../../../shared/components/modals/base_class/modal-base-class';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexLegend,
  ApexPlotOptions,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
} from 'ng-apexcharts';
import { Subscription } from 'rxjs';
import { ScheduleService } from '../../../../../state/modules/schedule/services/schedule.service';
import { ScanAreaComponent } from '../scan-area/scan-area.component';
import { ToasterService } from '../../../../../core/services/toaster.service';
import { AMPLITUDE_EVENT, AmplitudeService } from '../../../../../core/services/amplitude.service';
import { ALERT_MESSAGES } from '../../../../../constants/messages/message.constant';
import { PATROL_COLORS, PATROL_COMPLETED_COLORS, PATROL_RESPONSIBILITY_TYPE, PATROL_RESPONSIBILITY_TYPE_LABEL, PATROL_STATUS, PATROL_STATUS_BADGE_COLORS } from '../../../../../constants/lookup/location/patrol.enum';

import { LocalStorageUtil } from '../../../../../core/utils/local-storage-util';
import moment from 'moment';

// tslint:disable-next-line:interface-over-type-literal
export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  fill: ApexFill;
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  legend: ApexLegend;
  labels?: string[];
};

@Component({
  standalone: false,
  selector: 'kt-shift-patrol-list',
  templateUrl: './shift-patrol-list.component.html',
  styleUrls: ['./shift-patrol-list.component.scss'],
})
export class ShiftPatrolListComponent extends ModalBaseClass implements OnInit, AfterViewInit, OnDestroy {

  buttonConfig = BUTTON_CONFIG;
  loops = [];
  shiftInfo = null;
  timesheet = null;
  gaugeColor: string = PATROL_COMPLETED_COLORS.LOW;
  patrolDetail: any = {};
  chartOptions: Partial<ChartOptions>;
  subscriptions: Subscription[] = [];
  loading = signal(false);
  user_id = 0;
  hasScanned = false;
  isLocationShared = false;
  responsibilityTypeLabel = PATROL_RESPONSIBILITY_TYPE_LABEL;
  beforeShiftCheckpoints: any[] = [];
  duringShiftCheckpoints: any[] = [];
  afterShiftCheckpoints: any[] = [];
  showAfterShift = false;
  shiftStartFormatted = '';
  shiftEndFormatted = '';

  constructor(public activeModal: NgbActiveModal, private ngbModal: NgbModal, public scheduleService: ScheduleService, private toastr: ToasterService, private cdr: ChangeDetectorRef,
  ) {
    super(ngbModal);
  }

  ngOnInit() {
    if (this.patrolDetail) {
      this.makePatrolData(this.patrolDetail);
      this.updateChart();
    }
  }

  ngAfterViewInit() {
    // Accordion shim wires panel.accordion one CD cycle late; force the second
    // pass so the before/during/after status panels paint (not left empty).
    this.cdr.detectChanges();
  }

  updateChart() {
    if (!this.patrolDetail) return;

    this.isLocationShared = this.patrolDetail.responsibility_type === PATROL_RESPONSIBILITY_TYPE.SHARED_PATROL;

    const percent = this.patrolDetail.employee_completion_percentage;
    const fraction = `${this.patrolDetail.employee_scan_count}/${this.patrolDetail.employee_required_count}`;
    let fillColor = PATROL_COLORS.LOW;
    if (percent >= 80) {
      fillColor = PATROL_COLORS.HIGH;
      this.gaugeColor = PATROL_COMPLETED_COLORS.HIGH;
    }
    else if (percent >= 70) {
      fillColor = PATROL_COLORS.MED;
      this.gaugeColor = PATROL_COMPLETED_COLORS.MED;
    }

    this.chartOptions = {
      series: [percent],
      chart: {
        type: 'radialBar',
        height: 300
      },
      plotOptions: {
        radialBar: {
          hollow: { size: '45%' },
          dataLabels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: '600',
              color: '#A0A0A0',
              offsetY: 20
            },
            value: {
              show: true,
              fontSize: '32px',
              fontWeight: '700',
              color: '#111',
              offsetY: -15,
              formatter: () => fraction
            }
          }
        }
      },
      fill: {
        colors: [fillColor]
      },
      labels: ['Total Scan'],
    };
  }

  makeScanClickHandler() {
    new AmplitudeService().logEvent(AMPLITUDE_EVENT.ME_DASHBOARD_MAKE_SCAN, null);
    if (this.shiftInfo && this.shiftInfo.area && this.shiftInfo.area.area_type_manual_enable) {
      const compRef = this.openPopUp(ScanAreaComponent, { shiftInfo: this.shiftInfo, user_id: this.user_id }, { centered: true, size: 'lg', backdrop: 'static' });
      compRef.result.then((res) => {
        if (res && res.reload) {
          this.hasScanned = true;
          this.loading.set(true);
          this.getPatrolDetail();
        }
      }).catch(() => {});

    } else {
      this.toastr.errorShow(ALERT_MESSAGES.MANUAL_SCAN_ERROR, '', { closeButton: true, timeOut: 3000 });
    }
  }

  getPatrolDetail() {
    this.subscriptions.push(this.scheduleService.getShiftPatrolDetail({ timesheet_id: this.timesheet.timesheet_id }).subscribe(data => {
      if (data.success && data && data.data && data.data.data.patrol_detail) {
        this.makePatrolData(data.data.data.patrol_detail);
        this.updateChart();
        this.loading.set(false);
      }
      this.loading.set(false);
    }
      , (error) => {
        this.loading.set(false);
      }
    ));
  }

  makePatrolData(detail: any) {
    if (detail.checkpoints && Array.isArray(detail.checkpoints)) {
      detail.checkpoints = detail.checkpoints.map(cp => {
        if (cp.status) {
          const statusUI = this.getStatusConfig(cp.status);
          return {
            ...cp,
            displayStatus: statusUI.text,
            statusColor: statusUI.color
          };
        }
      });

    }
    this.patrolDetail = detail;
    this.groupCheckpointsByShiftWindow();
  }

  groupCheckpointsByShiftWindow() {
    this.beforeShiftCheckpoints = [];
    this.duringShiftCheckpoints = [];
    this.afterShiftCheckpoints = [];
    this.showAfterShift = false;

    if (!this.patrolDetail || !this.patrolDetail.checkpoints) return;

    const checkpoints = this.patrolDetail.checkpoints;
    const shiftStart = this.patrolDetail.shift_start_date ? new Date(this.patrolDetail.shift_start_date) : null;
    const shiftEnd = this.patrolDetail.shift_end_date ? new Date(this.patrolDetail.shift_end_date) : null;

    this.shiftStartFormatted = this.patrolDetail.shift_start_date ? this.formatTimeOnly(this.patrolDetail.shift_start_date) : '';
    this.shiftEndFormatted = this.patrolDetail.shift_end_date ? this.formatTimeOnly(this.patrolDetail.shift_end_date) : '';

    if (!shiftStart) {
      this.duringShiftCheckpoints = checkpoints;
      return;
    }

    checkpoints.forEach((cp: any) => {
      const cpEnd = cp.end_time ? new Date(cp.end_time) : null;
      const cpStart = cp.start_time ? new Date(cp.start_time) : null;

      if (cpEnd && cpEnd < shiftStart) {
        this.beforeShiftCheckpoints.push(cp);
      } else if (shiftEnd && cpStart && cpStart > shiftEnd) {
        this.afterShiftCheckpoints.push(cp);
      } else {
        this.duringShiftCheckpoints.push(cp);
      }
    });

    this.showAfterShift = shiftEnd !== null && this.afterShiftCheckpoints.length > 0;
  }

  getStatusConfig(status: string): { text: string, color: string } {
    const s = status.toUpperCase();

    switch (s) {
      case PATROL_STATUS.ON_TIME:
        return {
          text: 'On Time',
          color: PATROL_STATUS_BADGE_COLORS.ONTIME
        };
      case PATROL_STATUS.EARLY:
        return {
          text: 'Early',
          color: PATROL_STATUS_BADGE_COLORS.EARLY
        };
      case PATROL_STATUS.LATE:
        return {
          text: 'Late',
          color: PATROL_STATUS_BADGE_COLORS.LATE
        };
      case PATROL_STATUS.DUPLICATE:
        return {
          text: 'Duplicate',
          color: PATROL_STATUS_BADGE_COLORS.DUPLICATE
        };
      case PATROL_STATUS.MISSED:
        return {
          text: 'Missed',
          color: PATROL_STATUS_BADGE_COLORS.MISSED
        };
      case PATROL_STATUS.PENDING:
        return {
          text: 'Pending',
          color: PATROL_STATUS_BADGE_COLORS.PENDING
        };
      default:
        return {
          text: status,
          color: '#A0A0A0'
        };
    }
  }

  formatDateTime(date: string): string {
    if (!date) return '';
    const dateFormat = LocalStorageUtil.getDateFormat();
    const timeFormat = LocalStorageUtil.getTimeFormat();
    return moment(date).format(`${dateFormat} ${timeFormat}`);
  }

  formatTimeOnly(date: string): string {
    if (!date) return '';
    const timeFormat = LocalStorageUtil.getTimeFormat();
    return moment(date).format(timeFormat);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(sub => {
        sub.unsubscribe();
      });
    }
  }
}
